<template>
  <div class="pesquisa-geral-wrapper">
    <div
      v-if="!telaEstreita"
      class="pesquisa-geral-container"
      :class="{ 'pesquisa-geral-container--expandido': estaFocado || termoBusca }"
      ref="containerRef"
    >
      <form class="pesquisa-geral-input-wrapper" @submit.prevent>
        <i class="fa-solid fa-magnifying-glass pesquisa-geral-icone-lupa font-size-16 text-cinza-texto"></i>
        <input
          ref="inputRef"
          type="text"
          class="pesquisa-geral-input"
          :placeholder="placeholderAtual"
          :value="termoBusca"
          autocomplete="off"
          @input="aoDigitar"
          @focus="aoFocar"
          @blur="aoDesfocar"
          @keydown="aoApertarTecla"
        />
        <span v-if="!estaFocado && !termoBusca" class="pesquisa-geral-kbd">
          <kbd>Ctrl</kbd>
          <kbd>K</kbd>
        </span>
        <i v-if="termoBusca" class="fa-solid fa-xmark pesquisa-geral-botao-limpar" @mousedown.prevent="aoLimpar"></i>
      </form>
      <ul
        v-if="dropdownAberto && resultadosFiltrados.length > 0"
        class="pesquisa-geral-dropdown"
        :class="{ 'pesquisa-geral-dropdown--navegando-teclado': indiceAtivo >= 0 }"
        @mousemove="aoMoverMouse"
      >
        <li
          v-for="(item, indice) in resultadosFiltrados"
          :key="indice"
          class="pesquisa-geral-item-resultado"
          :class="{ 'pesquisa-geral-item-resultado--ativo': indice === indiceAtivo }"
          :data-toggle="mensagemTooltip(item) ? 'tooltip' : null"
          :data-placement="mensagemTooltip(item) ? 'bottom' : null"
          :data-custom-class="mensagemTooltip(item) ? 'custom-maino-tooltip' : null"
          :title="mensagemTooltip(item) || null"
          :ref="(el) => setItemRef(el, indice)"
        >
          <a
            :href="`/${item.action}`"
            :class="{ 'pesquisa-geral-link-desabilitado': !item.has_submenu }"
            @click="aoClicarResultado($event, item)"
          >
            {{ item.description }}
          </a>
        </li>
      </ul>
    </div>

    <div v-else class="pesquisa-geral-container">
      <form class="pesquisa-geral-input-wrapper" @submit.prevent>
        <i class="fa-solid fa-magnifying-glass pesquisa-geral-icone-lupa font-size-16 text-cinza-texto"></i>
        <input
          type="text"
          class="pesquisa-geral-input"
          :placeholder="placeholderAtual"
          readonly
          @focus="aoFocarTelaEstreita"
        />
        <span class="pesquisa-geral-kbd">
          <kbd>Ctrl</kbd>
          <kbd>K</kbd>
        </span>
      </form>
    </div>

    <ModalPesquisa :aberto="modalAberto" @fechar="modalAberto = false" />
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import ModalPesquisa from './ModalPesquisa.vue'
import usePesquisaGeral from './usePesquisaGeral'

export default {
  name: 'PesquisaGeral',
  components: { ModalPesquisa },
  setup() {
    const composable = usePesquisaGeral({ tratarEscape: true })

    const estaFocado = ref(false)
    const containerRef = ref(null)
    const modalAberto = ref(false)

    const mq = typeof window !== 'undefined' ? window.matchMedia('(max-width: 1409px)') : null
    const telaEstreita = ref(mq ? mq.matches : false)

    const placeholderAtual = computed(() => {
      if (estaFocado.value) {
        return composable.traduzir('interface.pesquisa_geral.placeholder_pesquisando', 'Qual funcionalidade você está procurando?')
      }
      return composable.traduzir('interface.pesquisa_geral.placeholder_inicial', 'Pesquisar')
    })

    function aoFocar() {
      estaFocado.value = true
      if (composable.termoBusca.value.length > 0) {
        composable.dropdownAberto.value = true
      }
    }

    function aoDesfocar() {
      if (!composable.termoBusca.value) {
        estaFocado.value = false
      }
    }

    function aoLimpar() {
      composable.aoLimpar()
      estaFocado.value = false
    }

    function aoClicarFora(evento) {
      if (containerRef.value && !containerRef.value.contains(evento.target)) {
        composable.dropdownAberto.value = false
      }
    }

    function aoFocarTelaEstreita(evento) {
      evento.target.blur()
      modalAberto.value = true
    }

    function aoMudarTamanhoTela(evento) {
      telaEstreita.value = evento.matches
    }

    function aoApertarCtrlK(evento) {
      if (evento.ctrlKey && evento.key === 'k') {
        evento.preventDefault()
        modalAberto.value = true
      }
    }

    function inicializarTooltips() {
      composable.itensRefs.value.forEach((elemento, indice) => {
        if (!elemento) return
        const item = composable.resultadosFiltrados.value[indice]
        if (!item || !composable.mensagemTooltip(item)) return

        const $elemento = $(elemento)
        $elemento.tooltip('dispose').tooltip({
          trigger: 'hover',
          html: true,
        })
        $elemento.off('show.bs.tooltip').on('show.bs.tooltip', function () {
          const customClass = $(this).data('custom-class')
          const instancia = $(this).data('bs.tooltip')
          if (customClass && instancia && instancia.$tip) {
            $(instancia.$tip).addClass(customClass)
          }
        })
      })
    }

    function destruirTooltips() {
      composable.itensRefs.value.forEach((elemento) => {
        if (!elemento) return
        try {
          $(elemento).tooltip('dispose')
        } catch (_) {
          // ignora: tooltip pode já ter sido destruído pelo Bootstrap
        }
      })
    }

    watch(composable.resultadosFiltrados, () => {
      destruirTooltips()
      composable.itensRefs.value = []
      nextTick(() => inicializarTooltips())
    })

    onMounted(() => {
      composable.carregarSubmenus()
      document.addEventListener('click', aoClicarFora)
      document.addEventListener('keydown', aoApertarCtrlK)

      if (mq) {
        mq.addEventListener('change', aoMudarTamanhoTela)
      }
    })

    onBeforeUnmount(() => {
      destruirTooltips()
      document.removeEventListener('click', aoClicarFora)
      document.removeEventListener('keydown', aoApertarCtrlK)

      if (mq) {
        mq.removeEventListener('change', aoMudarTamanhoTela)
      }
    })

    return {
      ...composable,
      estaFocado,
      containerRef,
      modalAberto,
      telaEstreita,
      placeholderAtual,
      aoFocar,
      aoDesfocar,
      aoLimpar,
      aoFocarTelaEstreita,
    }
  },
}
</script>
